import { RouterProvider } from "@tanstack/react-router";
import { ContextMenuProvider } from "mantine-contextmenu";
import "mantine-contextmenu/styles.layer.css";
import {
	defaultCssVariablesResolver,
	MantineProvider,
	type MantineTheme,
	v8CssVariablesResolver,
} from "@mantine/core";
import {
	DEFAULT_PRIMARY_COLORS,
	usePrimaryColorStore,
} from "@stores/usePrimaryColor";
import { Toaster } from "react-hot-toast";
import { router } from "@services/router/router";
import { attachMsalAuthSync } from "@config/auth/msalAuthSync";
import { msalInstance } from "@config/auth/msalConfig";
import { useEffect } from "react";
import {
	PORTAL_MODE_OPTIONS,
	usePortalModeStore,
} from "@stores/usePortalModeStore";
import { useDocumentTitle } from "@mantine/hooks";
import { trackVisitedRoutes } from "@services/router/trackVisitedRoutes";
import { theme } from "@utils/theme";
import { ExternalLinkPreferenceBridge } from "@components/app/ExternalLinkPreferenceBridge";

const hybridCssVariablesResolver = (resolvedTheme: MantineTheme) => {
	const defaultResolved = defaultCssVariablesResolver(resolvedTheme);
	const v8Resolved = v8CssVariablesResolver(resolvedTheme);

	return {
		...defaultResolved,
		variables: {
			...defaultResolved.variables,
			...v8Resolved.variables,
		},
		light: v8Resolved.light,
		dark: defaultResolved.dark,
	};
};

export const App: React.FC = () => {
	const primaryColor = usePrimaryColorStore((state) => state.primaryColor);
	const isPrimaryColorCustomized = usePrimaryColorStore(
		(state) => state.isPrimaryColorCustomized,
	);
	const viewMode = usePortalModeStore((state) => state.viewMode);
	const effectivePrimaryColor = isPrimaryColorCustomized
		? primaryColor
		: DEFAULT_PRIMARY_COLORS[viewMode];

	useDocumentTitle(
		PORTAL_MODE_OPTIONS.find((o) => o.key === viewMode)?.label ?? "Portal",
	);

	useEffect(() => attachMsalAuthSync(msalInstance), []);

	useEffect(() => {
		const unsubscribe = trackVisitedRoutes(router);
		return () => unsubscribe?.();
	}, []);

	return (
		<MantineProvider
			theme={{ ...theme, primaryColor: effectivePrimaryColor }}
			//cssVariablesResolver={hybridCssVariablesResolver}
		>
			<ContextMenuProvider>
				<ExternalLinkPreferenceBridge />
				<Toaster />
				<RouterProvider router={router} />
			</ContextMenuProvider>
		</MantineProvider>
	);
};
